import { createI18n, I18n } from 'vue-i18n'
import { useGlobalStore } from '@/stores/global-store'
import type { Language } from '@/types'
import { fetchTranslations } from '@/api'
import { Ref } from 'vue'
import { supportedLanguages, vuesticConstants } from '@/i18n/constants'

let i18nInstance: I18n

export async function initializeI18n(): Promise<I18n> {
  let savedLanguage: string = localStorage.getItem('language') as Language
  if (savedLanguage == 'undefined') {
    savedLanguage = 'en'
  }

  let messages = {}

  if (savedLanguage != 'nolang') {
    try {
      const langRes = await fetchTranslations(savedLanguage)
      messages = {
        [savedLanguage]: {
          ...vuesticConstants[savedLanguage],
          ...langRes,
        },
      }
    } catch (err) {
      messages = { [savedLanguage]: vuesticConstants[savedLanguage] ?? {} }
    }
  }

  const datetimeFormats: Record<string, any> = {}
  const numberFormats: Record<string, any> = {}

  supportedLanguages.forEach((lang) => {
    ;(datetimeFormats[lang] = {
      dateTime: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
      shortMonth: { year: 'numeric', month: 'short', day: 'numeric' },
      shortTime: { hour: '2-digit', minute: '2-digit', hour12: true },
    }),
      (numberFormats[lang] = {
        price: {
          style: 'decimal',
          useGrouping: true,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
        currency: {
          style: 'currency',
          currency: 'EUR',
          useGrouping: true,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      })
  })

  i18nInstance = createI18n({
    legacy: false,
    locale: savedLanguage,
    messages,
    datetimeFormats,
    numberFormats,
  })

  return i18nInstance
}

export function getI18n(): I18n {
  return i18nInstance
}

export async function setLanguageAndLoad(lang: string) {
  const globalStore = useGlobalStore()
  const fallbackLang = supportedLanguages.includes(lang) ? lang : 'en'

  if (!globalStore.languageLoaded) {
    let translations = {}

    try {
      const langRes = await fetchTranslations(fallbackLang)
      translations = {
        ...vuesticConstants[fallbackLang],
        ...langRes,
      }
      globalStore.languageLoaded = true
    } catch (error) {
      translations = vuesticConstants[fallbackLang] ?? {}
    }

    const i18n = getI18n()
    i18n.global.setLocaleMessage(fallbackLang, translations)
    ;(i18n.global.locale as Ref<string>).value = fallbackLang

    globalStore.language = fallbackLang as Language
    localStorage.setItem('language', fallbackLang)
  }
}
